import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle2 } from 'lucide-react';
import { LEASE_TYPES } from '../../lib/listingConstants';

const StepLease = ({ listing, patchDraft, onNext, saving }) => {
  const [months, setMonths] = useState(listing.lease_length_months ?? 12);
  const [earliest, setEarliest] = useState(listing.earliest_move_in_date || '');
  const [latest, setLatest] = useState(listing.latest_move_in_date || '');
  const [leaseType, setLeaseType] = useState(listing.lease_type || 'fixed_term');
  const [ontarioStandard, setOntarioStandard] = useState(
    listing.ontario_standard_lease_will_be_used !== false
  );
  const [breakPolicy, setBreakPolicy] = useState(listing.lease_break_policy || '');

  const canContinue = months && earliest && leaseType && ontarioStandard;

  const save = async () => {
    await patchDraft({
      lease_length_months: Number(months),
      earliest_move_in_date: earliest,
      latest_move_in_date: latest || null,
      lease_type: leaseType,
      ontario_standard_lease_will_be_used: !!ontarioStandard,
      lease_break_policy: breakPolicy || null,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Lease terms</h1>
        <p className="mt-1 text-sm text-slate-600">Keep it honest. We surface lease type and length on every card.</p>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Lease type</Label>
        <div className="grid grid-cols-2 gap-2">
          {LEASE_TYPES.map((lt) => (
            <button
              key={lt.value}
              type="button"
              onClick={() => setLeaseType(lt.value)}
              className={`px-3 py-3 rounded-xl border text-sm font-medium ${
                leaseType === lt.value ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
              }`}
              data-testid={`lease-type-${lt.value}`}
            >
              {lt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Lease length (months)</Label>
        <Input
          type="number"
          min="1"
          max="36"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
          className="h-12"
          data-testid="lease-months"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Earliest move-in</Label>
          <Input
            type="date"
            value={earliest}
            onChange={(e) => setEarliest(e.target.value)}
            className="h-12"
            data-testid="earliest-date"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Latest move-in (optional)</Label>
          <Input
            type="date"
            value={latest}
            onChange={(e) => setLatest(e.target.value)}
            className="h-12"
            data-testid="latest-date"
          />
        </div>
      </div>

      <Card className="bg-emerald-50 border-emerald-200/60">
        <CardContent className="p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ontarioStandard}
              onChange={(e) => setOntarioStandard(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300"
              data-testid="ontario-standard-lease"
            />
            <div>
              <p className="text-sm font-semibold text-slate-900">
                I will use the Ontario Standard Lease.
              </p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Sturent requires all Ontario landlords to use the government-provided
                Standard Form of Lease (RTB Form 2229E).
              </p>
            </div>
          </label>
        </CardContent>
      </Card>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">
          Lease break policy <span className="text-xs font-normal text-slate-500">(optional)</span>
        </Label>
        <textarea
          value={breakPolicy}
          onChange={(e) => setBreakPolicy(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="e.g. 60 days' notice. Tenant pays for 1 month or until a replacement is found."
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
          data-testid="break-policy"
        />
        <p className="text-xs text-slate-500 mt-1">{breakPolicy.length}/500</p>
      </div>

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-lease-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepLease;
